import { apiFetch } from './client.js';

/**
 * The client case file: what was done, found, or written down about a customer.
 *
 * Entries are added and never edited. A correction is another entry saying so,
 * because a file that can be rewritten proves nothing about what was known at
 * the time a decision was taken.
 */

export interface CaseEntry {
  id: string;
  /** ACTION | EVALUATION | NOTE | SCREENING | RISK | SOURCE | DOCUMENT */
  entryType: string;
  subjectType: string | null;
  subjectId: string | null;
  actorId: string;
  summary: string;
  body: string | null;
  data: Record<string, unknown> | null;
  occurredAt: string;
}

export interface NewCaseEntry {
  entryType: string;
  summary: string;
  body?: string;
  subjectType?: string;
  subjectId?: string;
  /** PERIODIC or TRIGGERED — only meaningful on an evaluation. */
  reviewKind?: 'PERIODIC' | 'TRIGGERED';
}

export interface ScreeningOutcome {
  result: string;
  clear: boolean;
  matchedLists: string[];
  screeningId: string;
}

export const screeningApi = {
  /** Run a fresh sanctions screening and file the result on the case file. */
  rescreen: (customerId: string, reason?: string): Promise<ScreeningOutcome> =>
    apiFetch(`/admin/customers/${customerId}/rescreen`, {
      method: 'POST',
      body: reason !== undefined && reason !== '' ? { reason } : {},
    }),
};

export const caseFileApi = {
  forCustomer: (customerId: string, entryType?: string): Promise<CaseEntry[]> =>
    apiFetch<CaseEntry[]>(
      `/admin/customers/${customerId}/case-file${
        entryType !== undefined && entryType !== '' ? `?entryType=${entryType}` : ''
      }`,
    ),

  forPayment: (paymentId: string): Promise<CaseEntry[]> =>
    apiFetch<CaseEntry[]>(`/admin/inbound-payments/${paymentId}/case-file`),

  add: (customerId: string, entry: NewCaseEntry): Promise<{ recorded: boolean }> =>
    apiFetch(`/admin/customers/${customerId}/case-file`, { method: 'POST', body: entry }),
};
